import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Routes from '../components/Routes';
import { useLanguage } from '../contexts/language';
import { SetupExperiences } from '../components/SetupExperiences';
import { SetupHeadphones } from '../components/SetupHeadphones';
import { SetupAudio } from '../components/SetupAudio';
import { SetupGps } from '../components/SetupGps';
import AdminControls from '../components/AdminControls';
import { useExperience } from '../contexts/experience';
import PwaInstallButton from '../components/PwaInstallButton';
import NavbarLogoMenu from '../components/NavbarLogoMenu';
import NavbarIconMenu from '../components/NavbarIconMenu';
import { useSession } from '../contexts/session';

const setupViews = (steps, language) =>
  steps.map(({ component: Component, path }, i) => {
    const next = steps[i + 1] ? steps[i + 1].path : `/experience?lang=${language}`;
    const back = steps[i - 1] ? steps[i - 1].path : null;
    return {
      path,
      render: (props) => <Component steps={steps} step={i + 1} next={next} back={back} {...props} />,
      exact: true,
    };
  });

export default function Setup() {
  const { language } = useLanguage();
  const { config, hasGeoSpots } = useExperience();
  const { lightOnboarding } = useSession();
  const { pathname } = useLocation();

  const views = useMemo(() => {
    if (!config) {
      return setupViews([{ path: '/setup/1', component: SetupExperiences }], language);
    }

    // if lightOnboarding is true, we skip setupHeadphones and SetuAudio step
    const setupSteps = lightOnboarding
      ? [{ path: '/setup/1', component: SetupExperiences }]
      : [
          { path: '/setup/1', component: SetupExperiences },
          { path: '/setup/2', component: SetupHeadphones },
          { path: '/setup/3', component: SetupAudio },
          ...(hasGeoSpots ? [{ path: '/setup/4', component: SetupGps }] : []),
        ];

    return setupViews(setupSteps, language);
  }, [lightOnboarding, config, hasGeoSpots, language]);

  const [showControlsMenu, setShowControlsMenu] = useState(false);

  // if lightOnboarding is false and we are not in the first setup step
  // hide hamburger menu and open menu when clicking on logo 5 times
  // otherwise show classic hamburger menu icon
  const logoMenuNavbar = useMemo(
    () => pathname !== '/setup/1' && !lightOnboarding,
    [pathname, lightOnboarding]
  );

  return (
    <>
      <PwaInstallButton />
      <Layout
        fullscreen
        framed
        top={
          logoMenuNavbar ? (
            <NavbarLogoMenu showControlsMenu={showControlsMenu} setShowControlsMenu={setShowControlsMenu} />
          ) : (
            <NavbarIconMenu setShowControlsMenu={setShowControlsMenu} />
          )
        }
      >
        <Routes views={views} />
      </Layout>
      <AdminControls
        show={showControlsMenu}
        onClose={() => setShowControlsMenu(false)}
        showLanguageSelector={!logoMenuNavbar}
      />
    </>
  );
}
